import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { CurrentUserContext, User } from '@modules/core/types';
import { updateCurrentUser } from '../services';
import { FETCH_DELAY } from '@modules/shared/constants';
import { CORE_CURRENT_USER_KEY, CORE_UPDATE_CURRENT_USER_KEY } from '../constants';

function useMutationCurrentUser(
  onSuccess: () => void,
  onError?: (error: unknown, variables: Partial<User>, context: CurrentUserContext | undefined) => void,
): UseMutationResult<string, unknown> {
  const queryClient = useQueryClient();

  const mutationFn = async (updatedUser: User): Promise<any> => {
    await updateCurrentUser(updatedUser);
  };

  const onMutateFn = async (updatedUser: User): Promise<any> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [CORE_UPDATE_CURRENT_USER_KEY] });

    const previousUser = queryClient.getQueryData([CORE_CURRENT_USER_KEY]) as User;

    // Optimistically update to the new value
    queryClient.setQueryData([CORE_UPDATE_CURRENT_USER_KEY], updatedUser);

    // Return a context object with the snapshotted value
    return { previousUser };
  };

  const onErrorFn = (err: unknown, variables: User, context: CurrentUserContext | undefined): void => {
    if (context === undefined) {
      return;
    }
    if (context.previousUser !== undefined) {
      queryClient.setQueryData([CORE_UPDATE_CURRENT_USER_KEY], context.previousUser);
    }
    onError?.(err, variables, context);
  };

  const onSettledFn = async (): Promise<void> => {
    setTimeout(() => {
      void queryClient.invalidateQueries([CORE_CURRENT_USER_KEY]);
    }, FETCH_DELAY);
  };

  return useMutation<string, unknown, any, any>([CORE_UPDATE_CURRENT_USER_KEY], mutationFn, {
    onMutate: onMutateFn,
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: onErrorFn,
    // Always refetch after error or success:
    onSettled: onSettledFn,
    onSuccess,
  });
}

export default useMutationCurrentUser;
